import type { RegistryCatalog, TagsList, CVEReport, Image, CatalogStats } from '@/types'

const API_BASE = ''

export async function fetchCatalog(): Promise<RegistryCatalog> {
  const res = await fetch(`${API_BASE}/v2/_catalog`)
  if (!res.ok) throw new Error('Failed to fetch catalog')
  return res.json()
}

export async function fetchTags(name: string): Promise<TagsList> {
  const res = await fetch(`${API_BASE}/v2/${name}/tags/list`)
  if (!res.ok) throw new Error(`Failed to fetch tags for ${name}`)
  return res.json()
}

export async function fetchCVEs(name: string, tag?: string): Promise<CVEReport> {
  const url = tag
    ? `${API_BASE}/api/v1/images/${name}/cves?tag=${tag}`
    : `${API_BASE}/api/v1/images/${name}/cves`
  const res = await fetch(url)
  if (!res.ok) throw new Error(`Failed to fetch CVEs for ${name}`)
  return res.json()
}

export async function fetchSBOM(name: string, tag: string): Promise<Blob> {
  const res = await fetch(`${API_BASE}/api/v1/images/${name}/sbom?tag=${tag}`)
  if (!res.ok) throw new Error(`Failed to fetch SBOM for ${name}:${tag}`)
  return res.blob()
}

export function getSBOMUrl(name: string, tag: string): string {
  return `${API_BASE}/api/v1/images/${name}/sbom?tag=${tag}`
}

export async function fetchStats(): Promise<CatalogStats> {
  const res = await fetch(`${API_BASE}/api/v1/stats`)
  if (!res.ok) {
    return {
      totalImages: 0,
      totalTags: 0,
      zeroCveImages: 0,
      lastScan: new Date().toISOString(),
    }
  }
  return res.json()
}

const IMAGE_METADATA: Record<string, Partial<Image>> = {
  'alpine': { category: 'base', description: 'Minimal Alpine Linux base image' },
  'wolfi-base': { category: 'base', description: 'Wolfi-based distroless image' },
  'static': { category: 'base', description: 'Static binary base (no libc)' },
  'node': { category: 'runtime', description: 'Node.js runtime' },
  'python': { category: 'runtime', description: 'Python runtime' },
  'go': { category: 'runtime', description: 'Go runtime' },
  'rust': { category: 'runtime', description: 'Rust runtime' },
  'java': { category: 'runtime', description: 'Java/OpenJDK runtime' },
  'php': { category: 'runtime', description: 'PHP runtime with FPM' },
  'nginx': { category: 'web', description: 'NGINX web server' },
  'caddy': { category: 'web', description: 'Caddy web server' },
  'httpd': { category: 'web', description: 'Apache HTTP Server' },
  'postgres': { category: 'database', description: 'PostgreSQL database' },
  'mysql': { category: 'database', description: 'MySQL database' },
  'redis': { category: 'database', description: 'Redis in-memory store' },
  'mariadb': { category: 'database', description: 'MariaDB database' },
  'git': { category: 'tool', description: 'Git version control' },
  'curl': { category: 'tool', description: 'curl HTTP client' },
}

export function enrichImageData(name: string): Partial<Image> {
  const baseName = name.split('/').pop() || name
  for (const [key, metadata] of Object.entries(IMAGE_METADATA)) {
    if (baseName.toLowerCase().includes(key)) {
      return metadata
    }
  }
  return { category: 'base', description: `Container image: ${name}` }
}
